import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles
} from "@material-ui/core";

declare global {
  interface Window {
    BarcodeDetector?: {
      new (options?: { formats?: string[] }): {
        detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>>;
      };
      getSupportedFormats?: () => Promise<string[]>;
    };
  }
}

interface PivotSerialScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onDetected: (serial: string) => void;
}

const extractSerial = (rawValue: string) => {
  const trimmed = rawValue.trim();
  const digits = trimmed.match(/\d+/g)?.join("") ?? "";
  return digits || trimmed;
};

export const PivotSerialScanner = ({ isOpen, onClose, onDetected }: PivotSerialScannerProps) => {
  const classes = useStyles();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const detectorRef = useRef<{ detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>> } | null>(null);
  const [status, setStatus] = useState("Point the camera at a QR code");
  const [unsupported, setUnsupported] = useState(false);

  const hasCameraSupport = useMemo(
    () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia,
    []
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const stopStream = () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };

    const scanFrame = async () => {
      if (cancelled || !videoRef.current || !detectorRef.current) {
        return;
      }

      try {
        if (videoRef.current.readyState >= 2) {
          const barcodes = await detectorRef.current.detect(videoRef.current);
          const rawValue = barcodes[0]?.rawValue;
          if (rawValue) {
            const serial = extractSerial(rawValue);
            if (serial) {
              stopStream();
              onDetected(serial);
              onClose();
              return;
            }
          }
        }
      } catch {
        setStatus("Scanning failed. Try moving closer to the QR code.");
      }

      animationFrameRef.current = window.requestAnimationFrame(scanFrame);
    };

    const startScanner = async () => {
      if (!hasCameraSupport || !window.BarcodeDetector) {
        setUnsupported(true);
        setStatus("QR scanning is not supported in this browser.");
        return;
      }

      try {
        const supportedFormats = await window.BarcodeDetector.getSupportedFormats?.();
        if (supportedFormats && !supportedFormats.includes("qr_code")) {
          setUnsupported(true);
          setStatus("QR scanning is not supported in this browser.");
          return;
        }

        detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false
        });

        if (cancelled) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        setUnsupported(false);
        setStatus("Point the camera at a QR code");
        animationFrameRef.current = window.requestAnimationFrame(scanFrame);
      } catch {
        setStatus("Camera access was denied or unavailable.");
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [hasCameraSupport, isOpen, onClose, onDetected]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Scan Serial QR</DialogTitle>
      <DialogContent>
        <div className={classes.preview}>
          <video ref={videoRef} className={classes.video} muted playsInline />
          {!hasCameraSupport || unsupported ? (
            <div className={classes.overlayMessage}>
              <Typography variant="body2">{status}</Typography>
            </div>
          ) : null}
        </div>
        <Typography variant="body2" color="textSecondary" className={classes.status}>
          {status}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  preview: {
    position: "relative",
    width: "100%",
    minHeight: 280,
    backgroundColor: "#111",
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  overlayMessage: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    textAlign: "center",
    color: "#fff",
    background: "rgba(0, 0, 0, 0.55)"
  },
  status: {
    marginTop: theme.spacing(1.5)
  }
}));
